"use client"

import { SessionProvider } from "next-auth/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useEffect, useState, type ReactNode } from "react"
import { installAuthSessionBridge } from "@/lib/authSession"

export default function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => new QueryClient())

  useEffect(() => installAuthSessionBridge(), [])

  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const r = event.reason as { isNetworkError?: boolean; name?: string } | undefined
      if (r && typeof r === "object" && r.isNetworkError) {
        // Same-origin `/api/proxy` or upstream outages often surface as "Load failed" in
        // LINE/LIFF; don't block the whole app with the Next.js dev error overlay.
        event.preventDefault()
      }
    }
    window.addEventListener("unhandledrejection", onUnhandledRejection)
    return () => window.removeEventListener("unhandledrejection", onUnhandledRejection)
  }, [])

  return (
    <SessionProvider
      /**
       * App auth is driven by `localStorage.auth_token`; NextAuth session fetch
       * to `/api/auth/session` can fail in LINE/LIFF and spam `ClientFetchError`.
       * Passing `session={null}` marks the provider as "already initialized" so
       * it skips the initial client `getSession()` network round-trip.
       */
      session={null}
      refetchInterval={0}
      refetchOnWindowFocus={false}
      refetchWhenOffline={false}
    >
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </SessionProvider>
  )
}

